import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PageEventData } from '@app/shared/models/paging.class';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-paging',
  templateUrl: './paging.component.html',
  styleUrls: ['./paging.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PagingComponent implements OnInit, OnChanges {
  @Input() pageIndex = 1;
  @Input() isFirstPage = false;
  @Input() totalCount = 1;
  @Input() pageSize = 10;
  @Input() cursor = '';
  @Input() nextCursorReview = '';
  @Input() previousCursorReview = '';
  @Input() prevCursor = '';
  @Output() changePage: EventEmitter<PageEventData> =
    new EventEmitter<PageEventData>();
  @Output() nextCursorClicked: EventEmitter<PageEventData> =
    new EventEmitter<PageEventData>();
  @Output() previousCursorClicked: EventEmitter<PageEventData> =
    new EventEmitter<PageEventData>();
  @Output() firstPageEvent: EventEmitter<PageEventData> =
    new EventEmitter<PageEventData>();

  isPreviousPageDisabled = true;
  isNextPageDisabled = true;
  totalNumberOfPages = 1;
  constructor(
    private translate: TranslateService,
    private http: HttpClient
  ) {}
  ngOnInit() {
    // Load and merge per-component translation file
    const lang =
      this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    this.http
      .get(`/assets/locale/${lang}/dashboard.json`)
      .subscribe((translations) => {
        this.translate.setTranslation(lang, translations, true);
      });

    // Set initial direction
    this.setDirection(lang);

    // Listen for language changes to update direction
    this.translate.onLangChange.subscribe((event) => {
      this.setDirection(event.lang);
    });

    this.totalNumberOfPages = this.getNumberOfPages();
    this.isPreviousPageDisabled = !this.hasPreviousPage();
    this.isNextPageDisabled = !this.hasNextPage();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes['totalCount'] && !changes['totalCount'].firstChange) {
        this.totalNumberOfPages = this.getNumberOfPages();
      }
      this.isPreviousPageDisabled = !this.hasPreviousPage();
      this.isNextPageDisabled = !this.hasNextPage();
    }
  }

  nextPage(nextCursor: string): void {
    if (!this.hasNextPage()) {
      return;
    }

    this.isFirstPage = false;
    const previousPageIndex = this.pageIndex;
    this.pageIndex++;
    this.emitNextCursorClicked(nextCursor, previousPageIndex);
  }

  previousPage(previousCursor: string): void {
    if (!this.hasPreviousPage()) {
      return;
    }
    this.isFirstPage = false;
    const previousPageIndex = this.pageIndex;
    this.pageIndex = this.pageIndex - 1;
    this.emitPreviousCursorClicked(previousCursor, previousPageIndex);
  }

  firstPage(): void {
    if (!this.hasPreviousPage()) {
      return;
    }
    this.isFirstPage = true;
    const previousPageIndex = this.pageIndex;
    this.pageIndex = 1;

    this.emitFirstPageEvent(this.isFirstPage, previousPageIndex);
  }

  hasPreviousPage(): boolean {
    return this.pageIndex > 1 && this.pageSize != 0;
  }

  hasNextPage(): boolean {
    const maxPageIndex = this.totalNumberOfPages;
    const it = this.pageIndex < maxPageIndex && this.pageSize !== 0;
    return it;
  }

  getNumberOfPages(): number {
    if (!this.pageSize) {
      return 0;
    }
    return Math.ceil(this.totalCount / this.pageSize);
  }

  private emitFirstPageEvent(
    firstPageBool: boolean,
    previousPageIndex: number
  ) {
    this.changePage.emit({
      previousPageIndex,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.totalCount,
      cursor: '',
      nextCursor: '',
      prevCursor: '',
      firstPage: firstPageBool,
    });
  }

  private emitNextCursorClicked(nextCursor: string, previousPageIndex: number) {
    this.changePage.emit({
      previousPageIndex,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.totalCount,
      cursor: this.cursor,
      nextCursor: nextCursor,
      prevCursor: '',
      firstPage: this.isFirstPage,
    });
  }

  private emitPreviousCursorClicked(
    previousCursor: string,
    previousPageIndex: number
  ) {
    this.changePage.emit({
      previousPageIndex,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.totalCount,
      cursor: this.cursor,
      nextCursor: '',
      prevCursor: previousCursor,
      firstPage: this.isFirstPage,
    });
  }

  private setDirection(lang: string) {
    if (lang === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }
}
